import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUserGraduate, FaTrophy, FaBook, FaKeyboard, FaNewspaper, FaCheck, FaStar, FaRocket } from 'react-icons/fa';

// Prénoms d'élèves français
const STUDENT_NAMES = [
  'Emma', 'Lucas', 'Léa', 'Hugo', 'Chloé', 'Nathan', 'Manon', 'Louis', 
  'Camille', 'Arthur', 'Jade', 'Raphaël', 'Louise', 'Gabriel', 'Alice',
  'Adam', 'Inès', 'Paul', 'Lina', 'Jules', 'Sarah', 'Enzo', 'Anna',
  'Théo', 'Clara', 'Noah', 'Eva', 'Mathis', 'Zoé', 'Maxime', 'Léna',
  'Tom', 'Rose', 'Clément', 'Juliette', 'Antoine', 'Margot', 'Baptiste',
  'Océane', 'Alexandre', 'Romane', 'Victor', 'Charlotte', 'Axel', 'Ambre'
];

// Types d'activités
const ACTIVITIES = [
  { type: 'completed_quiz', icon: FaTrophy, color: '#FFD700', messages: [
    'vient de terminer le Quiz Quotidien avec 87% !',
    'a obtenu 92% au Quiz d\'actualité !',
    'vient de scorer 85% sur le Quiz du jour !',
    'a brillé avec 90% au Quiz Quotidien !'
  ]},
  { type: 'started_course', icon: FaBook, color: '#4CAF50', messages: [
    'commence le cours "Histoire de la Presse"',
    'démarre le module "Économie des Médias"',
    'se lance dans "Style & Expressions"',
    'attaque le cours SEO'
  ]},
  { type: 'completed_level', icon: FaStar, color: '#E91E63', messages: [
    'passe au niveau 5 du cours Dactylo !',
    'débloque le niveau 3 de Grammaire !',
    'atteint le niveau 7 en frappe rapide !',
    'monte au niveau 4 en SEO !'
  ]},
  { type: 'typing_score', icon: FaKeyboard, color: '#2196F3', messages: [
    'atteint 65 mots/minute en Dactylo !',
    'nouveau record : 72 mots/minute !',
    'frappe maintenant à 58 mots/minute !',
    'améliore sa vitesse : 61 mots/minute !'
  ]},
  { type: 'badge_earned', icon: FaRocket, color: '#9C27B0', messages: [
    'décroche le badge "Expert Actu" !',
    'obtient le badge "Grammairien" !',
    'gagne le badge "Journaliste en herbe" !',
    'reçoit le badge "Champion SEO" !'
  ]},
  { type: 'exercise_done', icon: FaCheck, color: '#00BCD4', messages: [
    'termine 10 exercices de grammaire',
    'valide 5 exercices d\'expressions',
    'complète la série d\'anglicismes',
    'finit les exercices de pléonasmes'
  ]},
  { type: 'who_is_who', icon: FaNewspaper, color: '#FF5722', messages: [
    'reconnaît 8 personnalités politiques !',
    'identifie 10 journalistes célèbres !',
    'score parfait sur les patrons de presse !',
    'maîtrise les personnalités médias !'
  ]}
];

function LiveActivityNotifications({ maxNotifications = 4 }) {
  const [notifications, setNotifications] = useState([]);

  const generateNotification = () => {
    const student = STUDENT_NAMES[Math.floor(Math.random() * STUDENT_NAMES.length)];
    const activity = ACTIVITIES[Math.floor(Math.random() * ACTIVITIES.length)];
    const message = activity.messages[Math.floor(Math.random() * activity.messages.length)];
    
    return {
      id: Date.now() + Math.random(),
      student,
      message,
      icon: activity.icon,
      color: activity.color,
      timestamp: new Date()
    };
  };

  useEffect(() => {
    // Générer quelques notifications initiales
    const initial = Array.from({ length: 3 }, () => generateNotification());
    setNotifications(initial);

    // Ajouter une nouvelle notification toutes les 4-8 secondes
    const interval = setInterval(() => {
      const newNotif = generateNotification();
      setNotifications(prev => {
        const updated = [newNotif, ...prev];
        return updated.slice(0, maxNotifications);
      });
    }, 4000 + Math.random() * 4000);

    return () => clearInterval(interval);
  }, [maxNotifications]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="comic-text text-lg flex items-center">
          <FaUserGraduate className="mr-2 text-green-500" />
          Activité en direct
        </h3>
        <div className="flex items-center">
          <span className="relative flex h-3 w-3 mr-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span className="text-xs text-green-600 font-medium">EN DIRECT</span>
        </div>
      </div>
      
      <AnimatePresence mode="popLayout">
        {notifications.map((notif) => (
          <motion.div
            key={notif.id}
            initial={{ opacity: 0, x: -50, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 50, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className="flex items-center p-3 bg-white rounded-lg border-l-4 shadow-sm"
            style={{ borderLeftColor: notif.color }}
          >
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0"
              style={{ backgroundColor: `${notif.color}20` }}
            >
              <notif.icon style={{ color: notif.color }} className="text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm">
                <span className="font-bold text-gray-800">{notif.student}</span>
                <span className="text-gray-600 ml-1">{notif.message}</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">À l'instant</p>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
      
      <motion.div 
        className="text-center pt-2"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <p className="text-xs text-gray-500">
          <span className="font-bold text-green-600">47 élèves</span> actifs en ce moment
        </p>
      </motion.div>
    </div>
  );
}

export default LiveActivityNotifications;
